import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { UserProfile, VisitBlackoutDto } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import { useBlackouts, useDeleteBlackout } from "@/hooks/useVisitAvailability";
import { AddBlackoutDialog } from "./AddBlackoutDialog";
import { formatMinuteOfDay } from "./visit-status";

function formatDate(dateStr: string): string {
  // Noon UTC keeps the calendar date stable in any display timezone.
  return new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${dateStr}T12:00:00Z`));
}

function blackoutWhen(b: VisitBlackoutDto): string {
  const dates =
    b.dateFrom === b.dateTo
      ? formatDate(b.dateFrom)
      : `${formatDate(b.dateFrom)} – ${formatDate(b.dateTo)}`;
  const hours =
    b.startMinute !== null && b.endMinute !== null
      ? ` · ${formatMinuteOfDay(b.startMinute)} a ${formatMinuteOfDay(b.endMinute)}`
      : " · Todo el día";
  return dates + hours;
}

// Inline two-step confirm (BlockContactButton pattern) — a full dialog is
// overkill for removing an exception.
function DeleteBlackoutButton({ id }: { id: string }): React.JSX.Element {
  const deleteBlackout = useDeleteBlackout();
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <button
        type="button"
        onBlur={() => setConfirming(false)}
        onClick={() => deleteBlackout.mutate(id)}
        disabled={deleteBlackout.isPending}
        className="rounded px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50"
      >
        ¿Eliminar?
      </button>
    );
  }
  return (
    <button
      type="button"
      aria-label="Eliminar excepción"
      onClick={() => setConfirming(true)}
      className="rounded p-1.5 text-ink-400 hover:bg-ink-050 hover:text-red-600"
    >
      <Trash2 size={14} />
    </button>
  );
}

export function BlackoutsSection({ advisors }: { advisors: UserProfile[] }): React.JSX.Element {
  const { data, isLoading } = useBlackouts();
  const [addOpen, setAddOpen] = useState(false);
  const blackouts = data?.blackouts ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink-700">Excepciones</h3>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus size={13} className="mr-1" /> Agregar excepción
        </Button>
      </div>

      {isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-ink-100" />
      ) : blackouts.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-200 px-4 py-6 text-center text-[13px] text-ink-400">
          Sin excepciones próximas. Agrega vacaciones, festivos o salidas para
          bloquear horarios.
        </p>
      ) : (
        <ul className="overflow-hidden rounded-lg border border-ink-100 bg-white">
          {blackouts.map((b) => (
            <li
              key={b.id}
              className="flex items-center justify-between gap-3 border-b border-ink-050 px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-ink-900">
                  {b.userId === null ? "Toda la escuela" : b.advisorName ?? "Asesor"}
                </p>
                <p className="text-[12px] text-ink-500">
                  {blackoutWhen(b)}
                  {b.reason ? ` · ${b.reason}` : ""}
                </p>
              </div>
              <DeleteBlackoutButton id={b.id} />
            </li>
          ))}
        </ul>
      )}

      {addOpen && (
        <AddBlackoutDialog open onOpenChange={setAddOpen} advisors={advisors} />
      )}
    </div>
  );
}
